<template>
<n-button
    secondary
    class="mr-2 mb-3 rounded-md"
    :loading="checkBalanceLoading"
    :disabled="checkBalanceLoading"
    @click="onInsuranceOrder">
    {{ stats.localization['insurance_order_open_btn'] }}
</n-button>

<base-modal-desktop
    v-model:show="show"
    width="450px"
    :label="stats.localization['insurance_order_modal_title']">
    <template #default>
        <n-alert class="rounded-md mb-4" type="warning" size="small">
            <div class="notes text-xs">
                {{ stats.localization['insurance_order_txt']  }}
            </div>
        </n-alert>

        <section class="flex items-end">
            <rb-select
                :label="stats.localization['insurance_order_price_label']"
                class="flex-grow"
                :options="orderExecutionTypes"
                v-model:value="orderExecution" />
            
            <rb-input
                v-if="!model.market"
                class="ml-2"
                type="longprice"
                v-model:value="model.rate"/>
        </section>

        <section class="flex items-center">
            <span>{{ stats.localization['trade_statistics_management_current'] }}</span>
            <span class="mx-4 rb-td-stock-icon w-12" :class="`rb-td-stock-icon-${exchange(botShortInfo?.exchange)?.code_name}`"></span>
            <span>{{ stats.localization['trade_statistics_management_tickers_for'] }}</span>
            <div class="ml-4 flex items-center">
                <rb-coin class="w-6" :coin="currency(botShortInfo?.tickers_for?.split('/').shift().trim())" />
                <div class="ml-2">{{ botShortInfo?.tickers_for }}</div>
            </div>
        </section>
        <section class="flex">
            <n-card
                size="small"
                class="mr-2 shadow border dark:border-darkCardBg bg-transparent rounded-md padding-2 w-6/12"
                content-style="padding: 4px 8px;"
                :segmented="{
                    content: true,
                    footer: 'soft'
                }">
                <template #default>
                    <section class="flex items-center">
                        <div>{{ stats.localization['trade_statistics_management_currenttickers_buy'] }}</div>
                        <div
                            class="text-green-600 font-semibold text-lg ml-2"
                            :class="!model.market ? 'underline cursor-pointer hover:no-underline' : ''"
                            @click="setPrice(botShortInfo.buy)">
                            &nbsp;{{ botShortInfo?.buy }}
                        </div>
                    </section>
                </template>
            </n-card>
            
            <n-card
                size="small"
                class="ml-2 shadow border dark:border-darkCardBg bg-transparent rounded-md padding-2 w-6/12"
                content-style="padding: 4px 8px;"
                :segmented="{
                    content: true,
                    footer: 'soft'
                }">
                <template #default>
                    <section class="flex items-center">
                        <div>{{ stats.localization['trade_statistics_management_currenttickers_sell'] }}</div>
                        <div
                            class="text-red-500 font-semibold text-lg ml-2"
                            :class="!model.market ? 'underline cursor-pointer hover:no-underline' : ''"
                            @click="setPrice(botShortInfo.sell)">
                            &nbsp;{{ botShortInfo?.sell }}
                        </div>
                    </section>
                </template>
            </n-card>
        </section>
        <n-divider />

        <n-statistic
            v-if="symbolBalance"
            tabular-nums
            class="w-6/12 text-left"
            :label="stats.localization['insurance_order_available_value']">
            <n-number-animation
                :from="0"
                :to="+symbolBalance"
                :precision="8"
            />
        </n-statistic>

        <section class="flex items-end mt-4">
            <rb-input
                type="longprice"
                :label="stats.localization['insurance_order_size_label']"
                v-model:value="model.amount">
                <template #beforeInput v-if="botShortInfo.futures">
                    <div class="text-main font-semibold whitespace-nowrap text-lg pr-3">{{ botShortInfo?.leverage }}x</div>
                </template>
            </rb-input>
            
            <div class="flex flex-col items-center ml-2">
                <rb-coin
                    class="w-6"
                    :coin="currency(coin)" />
                <div class="text-gray-400 dark:text-white/55 text-center whitespace-nowrap uppercase text-xs -mt-1">
                    {{ coin }}
                </div>
            </div>
        </section>

         <section class="flex mt-4">
            <section v-if="r1 || r2" class="w-6/12 flex flex-col items-start flex-grow">
                <n-statistic
                    tabular-nums
                    class="text-left"
                    :label="stats.localization?.current_price_of_take_profit_order_label || 'Текущая цена тейк профит ордера'">
                    <n-number-animation
                        :from="0"
                        :to="r2"
                        :precision="8"
                    />
                </n-statistic>
                <rb-label
                    noWrap
                    class="w-min"
                    :label="stats.localization?.current_price_to_breakeven_price_label || 'Текущая цена в безубыток'" />
                <div
                    :class="r1 > 0 ? 'text-green-600' : 'text-red-500'">
                    {{ r1 }}
                </div> 
            </section>
            
            <section v-if="takeprofitShow" class="flex flex-col items-end flex-grow">
                <n-statistic
                    tabular-nums
                    class="text-right"
                    :label="stats.localization?.new_price_takeprofit_label || 'Новая цена ордера тейк профит'">
                    <n-number-animation
                        class="text-xs"
                        :from="0"
                        :to="takeprofit"
                        :precision="8"
                    />
                </n-statistic>
                <rb-label
                    noWrap
                    class="w-min"
                    :label="stats.localization?.breakeven_price_label || 'Безубыток'" />
                <div
                    :class="priceToZero > 0 ? 'text-green-600' : 'text-red-500'">
                    {{ priceToZero }}
                </div> 
            </section>
         </section>

    </template>
    <template #footer>
        <div class="flex justify-end items-center">
            <n-button
                :color="gl.mainColor"
                :loading="addInsuranceOrderLoading"
                :disabled="addInsuranceOrderLoading"
                class="text-white/90 rounded-md"
                @click="onAddInsuranceOrder">
                {{ stats.localization['insurance_order_action_btn'] }}
            </n-button>
        </div>
    </template>
</base-modal-desktop>
</template>

<script>
// general
import general from './general';

// components
import RbCoin from '@components/rb-coin';
import RbInput from '@components/rb-input';
import RbSelect from '@components/rb-select';
import RbCheckbox from '@components/rb-checkbox';

// UI
import {
    NCard,
    NAlert,
    NButton,
    NDivider,
    NStatistic,
    NNumberAnimation } from 'naive-ui';

export default {
    name: 'insurance-order',
    props: {
        botShortInfo: {
            type: Object,
            required: true,
        },
        averagePrice: {
            type: Number,
            required: true,
        },
        activeCycle: {
            type: Object,
            required: true,
        },
    },
    components: {
        NCard,
        RbCoin,
        NAlert,
        NButton,
        RbInput,
        NDivider,
        RbSelect,
        RbCheckbox,
        NStatistic,
        NNumberAnimation,
    },
    setup(props, context) {
        
        return {
            ...general(props, context),
        };
    },
};
</script>